import { Field, Form, Formik, FormikHelpers } from 'formik'
import { FC, ReactElement, useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import * as yup from 'yup'
import Button from '../../components/button'
import Input from '../../components/input'
import { useAuth } from '../../hooks/auth'
import { obfuscateEmail } from '../../utils/obfuscateEmail'
import { RoutesEnum } from '../../utils/routesEnum'
import LoginSkeleton from '../login/skeleton'
import { Center, VerificationCode } from '../register/style'

const schema = yup.object().shape({
  code: yup.string().required('Digite o código'),
  password: yup.string()
    .required('Senha obrigatória.')
    .min(6, 'Senha muito curta.')
    .matches(/[a-zA-Z]/, 'Ao menos uma letra é obrigatória.'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password'), null], 'As senhas devem ser iguais')
})

const ForgotPasswordVerificationCode: FC = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)

  const { forgotPassword, askCode, resetPassword } = useAuth()

  const email = forgotPassword?.email

  const informationEmail = `Codigo de verificação enviado para: ${obfuscateEmail(email)}.`

  const history = useHistory()

  useEffect(() => { if (!email) history.push(RoutesEnum.FORGOT_PASSWORD) }, [email])

  const initialValues = {
    code: '',
    password: '',
    confirmPassword: ''
  }

  const submit = async (values: typeof initialValues, helpers: FormikHelpers<typeof initialValues>): Promise<void> => {
    helpers.setSubmitting(true)
    try {
      if (email) {
        await resetPassword(email, values.confirmPassword, values.code)
        history.push(RoutesEnum.LOGIN)
      }
    } catch { }
    helpers.setSubmitting(false)
  }

  const resendEmail = useCallback(async () => {
    if (email) {
      setIsLoading(true)
      try {
        await askCode(email)
      } catch {}
      setIsLoading(false)
    }
  }, [email, setIsLoading])

  return (
    <LoginSkeleton title='Recuperação de senha por e-mail'>
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={submit}
      >
        {({ isSubmitting }) => (
          <Form>
            <Center>
              <p><b>Verificação de segurança</b></p>
              <span>Conclua a verificação a seguir para proteger a sua conta.</span>
            </Center>
            <VerificationCode>
              <Field name='code' component={Input} label='Copie e cole o código de verificação aqui' info={informationEmail} />
            </VerificationCode>
            <Field name='password' component={Input} label='Nova Senha' type='password' />
            <Field name='confirmPassword' component={Input} label='Confirmar Nova Senha' type='password' />
            <div className='buttons'>
              <Button block isLoading={isSubmitting} type='submit'>ENVIAR</Button>
              <br />
              <Button
                variant='link'
                block
                size='sm'
                isLoading={isLoading}
                onClick={resendEmail}
              >Não recebeu o código? Enviar novamente!
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </LoginSkeleton>
  )
}

export default ForgotPasswordVerificationCode
